/**
 * The header. §6.2.
 *
 * Canva's arrangement in the shop's own colours: the shop's mark, a File menu,
 * undo and redo, whether the work is saved; the design's name in the middle
 * with what was chosen for it; and the two things a customer came to do on the
 * right. Save and Add to basket are the only saturated things up here.
 */

import { useEffect, useRef, useState } from 'react';
import { useCanRedo, useCanUndo, useEditor } from '../state/store';
import { IconChevron, IconRedo, IconUndo } from './icons';
import { UNITS } from '../model/units';
import type { TemplateMode } from '../api';
import { CornersChip, PagesChip } from './Choices';

interface Props {
  /** What the customer is designing, e.g. "Business Cards". */
  product: string;
  /** What the customer chose on the product page, if it was a variation. */
  chose?: Array<{ label: string; value: string }>;
  /** Whose editor this is. */
  shop?: { name: string; logo: string; home: string };
  /** The design has not arrived yet. */
  opening: boolean;
  saving: boolean;
  savedAt: number | null;
  onSave: () => void;
  onRename: ( title: string ) => void;
  onAddToBasket: () => void;
  /** Opens a panel from the File menu. */
  onOpen: ( which: 'projects' | 'settings' ) => void;
  onHelp: () => void;
  returnUrl: string;
  canOrder: boolean;
  /** Designing without an account: kept in the browser, Save asks to sign in. */
  guest?: boolean;
  onSignIn?: () => void;
  /** The shop drawing one of its templates, rather than a customer a card. */
  template?: TemplateMode;
  /** Finished with the template: save it and go back to its screen. */
  onDone?: () => void;
  /** Something the customer needs to be told - a page that could not be removed. */
  onProblem?: ( said: string ) => void;
  /** Makes the stamped proof and opens it. */
  onProof?: () => void;
  /** Bring a Photoshop, Illustrator or SVG file in as layers. Templates only. */
  onImport?: () => void;
  /** Opens or closes the 3D preview; absent when the product has no 3D. */
  onThree?: () => void;
  threeOpen?: boolean;
  /** The preview opens on a room first, so the button is not only 3D any more. */
  previewInRoom?: boolean;
  /** The design is already in the basket: the button updates it. */
  inBasket?: boolean;
}

export function TopBar( props: Props ) {
  const { product, chose, shop, opening, saving, savedAt, onSave, onRename, onAddToBasket, canOrder, guest, onSignIn, template, onDone, onProblem } = props;
  const design = useEditor( ( s ) => s.design );
  const dirty = useEditor( ( s ) => s.dirty );
  const undo = useEditor( ( s ) => s.undo );
  const redo = useEditor( ( s ) => s.redo );
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();

  const [ title, setTitle ] = useState( design?.title ?? '' );

  useEffect( () => setTitle( design?.title ?? '' ), [ design?.uuid, design?.title ] );

  return (
    <header className="tb-bar">
      <a className="tb-bar__mark" href={ shop?.home || '/' } title={ shop?.name ? `Back to ${ shop.name }` : 'Home' }>
        { shop?.logo ? (
          <img className="tb-bar__logo" src={ shop.logo } alt={ shop.name } />
        ) : (
          <>
            <span className="tb-bar__dot" aria-hidden="true" />
            { shop?.name || 'Design' }
          </>
        ) }
      </a>

      <FileMenu { ...props } />

      <span className="tb-bar__sep" role="separator" />

      <button type="button" className="tb-bar__icon" onClick={ undo } disabled={ ! canUndo } title="Undo (Ctrl+Z)" aria-label="Undo">
        <IconUndo size={ 20 } />
      </button>
      <button type="button" className="tb-bar__icon" onClick={ redo } disabled={ ! canRedo } title="Redo (Ctrl+Y)" aria-label="Redo">
        <IconRedo size={ 20 } />
      </button>

      {/* Politely, and out of the way. A save indicator that shouts is a save
          indicator nobody trusts. */}
      <span className={ `tb-bar__state${ dirty ? '' : ' is-saved' }${ guest ? ' is-local' : '' }` } aria-live="polite">
        { opening
          ? 'Opening…'
          : saving
            ? 'Saving…'
            : dirty
              ? 'Unsaved changes'
              : guest
                ? 'Kept in this browser'
                : savedAt
                  ? `Saved ${ clock( savedAt ) }`
                  : 'All changes saved' }
      </span>

      <div className="tb-bar__what">
        <input
          className="tb-bar__title"
          value={ title }
          aria-label="Design name"
          placeholder="Untitled design"
          // A template is named on its own screen in the admin, where the
          // customers' gallery takes the name from.
          disabled={ opening || !! template }
          onChange={ ( event ) => setTitle( event.target.value ) }
          onBlur={ () => title.trim() && title !== design?.title && onRename( title.trim() ) }
          onKeyDown={ ( event ) => {
            if ( event.key === 'Enter' ) event.currentTarget.blur();
            if ( event.key === 'Escape' ) {
              setTitle( design?.title ?? '' );
              event.currentTarget.blur();
            }
          } }
        />
        <span className="tb-bar__product">
          { template ? (
            <span className="tb-bar__badge" title="You are drawing a template customers can start from">Template</span>
          ) : (
            product
          ) }

          {/*
            What the customer picked on the product page. Quiet, because it is
            a reminder rather than a control - changing it means going back to
            the product and choosing again.
          */}
          { chose && chose.length > 0 && (
            <span className="tb-bar__chose">
              { chose.map( ( each ) => (
                <span className="tb-bar__chip" key={ each.label } title={ each.label }>
                  { each.value }
                </span>
              ) ) }
            </span>
          ) }

          {/* What the customer can change here as well as on the product page. */}
          { ! template && <CornersChip /> }
          { ! template && <PagesChip onProblem={ onProblem } /> }
        </span>
      </div>

      { props.onThree && (
        <button
          type="button"
          className={ `tb-btn tb-bar__three${ props.threeOpen ? ' is-on' : '' }` }
          aria-pressed={ !! props.threeOpen }
          title={ props.previewInRoom ? 'See your design in a room, or in 3D' : 'See your design on the product in 3D' }
          onClick={ props.onThree }
          disabled={ opening }
        >
          { props.previewInRoom ? 'Preview' : '3D' }
        </button>
      ) }

      { guest && onSignIn && (
        <button type="button" className="tb-btn tb-btn--quiet tb-bar__signin" onClick={ onSignIn }>
          Sign in
        </button>
      ) }

      {/* A visitor can always press Save - it is how they are asked to sign in. */}
      <button type="button" className="tb-btn tb-bar__save" onClick={ onSave } disabled={ opening || saving || ( ! dirty && ! guest ) }>
        Save
      </button>

      { template ? (
        <button type="button" className="tb-btn tb-btn--primary tb-bar__order tb-bar__done" onClick={ onDone } disabled={ opening || saving }>
          Done
        </button>
      ) : (
        <button type="button" className="tb-btn tb-btn--primary tb-bar__order" onClick={ onAddToBasket } disabled={ opening || saving || ! canOrder }>
          { props.inBasket ? 'Update basket' : 'Add to basket' }
        </button>
      ) }
    </header>
  );
}

/** The File menu: saving, the design's pages and views, and the way out. */
function FileMenu( { onSave, onOpen, onHelp, onProof, onImport, returnUrl, opening, template }: Props ) {
  const [ open, setOpen ] = useState( false );
  const wrap = useRef<HTMLDivElement>( null );
  const view = useEditor( ( s ) => s.view );
  const setView = useEditor( ( s ) => s.setView );
  const unit = useEditor( ( s ) => s.unit );
  const setUnit = useEditor( ( s ) => s.setUnit );

  useEffect( () => {
    if ( ! open ) return;

    const away = ( event: MouseEvent ) => {
      if ( wrap.current && ! wrap.current.contains( event.target as Node ) ) setOpen( false );
    };

    const key = ( event: KeyboardEvent ) => {
      if ( event.key !== 'Escape' ) return;

      event.preventDefault();
      setOpen( false );
    };

    window.addEventListener( 'mousedown', away );
    window.addEventListener( 'keydown', key, true );

    return () => {
      window.removeEventListener( 'mousedown', away );
      window.removeEventListener( 'keydown', key, true );
    };
  }, [ open ] );

  const item = ( label: string, run: () => void, keys?: string, on?: boolean ) => (
    <button
      type="button"
      role={ on === undefined ? 'menuitem' : 'menuitemcheckbox' }
      aria-checked={ on }
      className="tb-menu__item"
      onClick={ () => {
        run();
        setOpen( false );
      } }
    >
      <span className="tb-menu__tick" aria-hidden="true">{ on ? '✓' : '' }</span>
      <span className="tb-menu__label">{ label }</span>
      { keys && <kbd className="tb-menu__keys">{ keys }</kbd> }
    </button>
  );

  return (
    <div className="tb-bar__file" ref={ wrap }>
      <button type="button" className="tb-bar__menu" aria-haspopup="menu" aria-expanded={ open } disabled={ opening } onClick={ () => setOpen( ! open ) }>
        File <IconChevron size={ 14 } />
      </button>

      { open && (
        <div className="tb-menu" role="menu" aria-label="File">
          { item( 'Save', onSave, 'Ctrl S' ) }
          { item( 'My designs', () => onOpen( 'projects' ), 'D' ) }
          { item( 'Page settings', () => {
            useEditor.getState().select( [] );
            onOpen( 'settings' );
          } ) }
          { onProof && item( 'Download a proof', onProof ) }
          {/* The shop's own tool: a template is often drawn elsewhere first. */}
          { template && onImport && item( 'Import artwork…', onImport ) }
          <hr className="tb-menu__rule" />
          { item( 'Show rulers', () => setView( { showRulers: ! view.showRulers } ), 'R', view.showRulers ) }
          { item( 'Show grid', () => setView( { showGrid: ! view.showGrid } ), 'G', view.showGrid ) }
          { item( 'Show guides', () => setView( { showGuides: ! view.showGuides } ), ';', view.showGuides ) }
          { item( 'Clear the guides', () => useEditor.getState().clearGuides() ) }
          { item( 'Preview', () => setView( { preview: ! view.preview } ), 'W', view.preview ) }

          {/* Every length in the editor, not only the rulers. The menu stays
              open, so the change can be seen before it closes. */}
          <div className="tb-menu__units" role="group" aria-label="Units">
            <span className="tb-menu__label">Units</span>
            <span className="tb-menu__segs">
              { UNITS.map( ( each ) => (
                <button
                  key={ each.id }
                  type="button"
                  role="menuitemradio"
                  aria-checked={ each.id === unit }
                  title={ each.name }
                  className={ `tb-menu__seg${ each.id === unit ? ' is-on' : '' }` }
                  onClick={ () => setUnit( each.id ) }
                >
                  { each.short }
                </button>
              ) ) }
            </span>
          </div>
          <hr className="tb-menu__rule" />
          { item( 'Keyboard shortcuts', onHelp, '?' ) }
          { template
            ? item( 'Back to the template', () => {
                window.location.href = template.editUrl;
              } )
            : item( 'Back to the product', () => {
                window.location.href = returnUrl;
              } ) }
        </div>
      ) }
    </div>
  );
}

function clock( at: number ): string {
  return new Date( at ).toLocaleTimeString( [], { hour: '2-digit', minute: '2-digit' } );
}
